import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalculatedColumn } from '@bst/react-data-grid';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';

import { useStores } from '../../../stores/useStores';
import { FilterType } from '../../../apiclients/api/types/FilterType';
import { RoleFilter } from '../../../stores/UserManagement/constants';
import { UserManagementFilter } from '../../../stores/UserManagement/types';
import { UserExistingRolesResponseDTO } from '../../../apiclients/bstinsights';

import { FilterCheckbox } from './utils/types';
import { FILTER_DEBOUNCE_TIMER, INDETERMINATE, SELECT_ALL } from './utils/constants';

/**
 * @property column - CalculatedColumn; Individual column from DataGrid columns
 * @property columnKey - string; Column key to map data key
 */
type Props<Row, SummaryRow> = {
  column: CalculatedColumn<Row, SummaryRow>;
  columnKey: string;
  existingRolesCount?: UserExistingRolesResponseDTO
};


/**
 * Hook for Cell Role Filter component
 */
export const useCellRoleFilter = <Row, SummaryRow = unknown>({ column, columnKey, existingRolesCount }: Props<Row, SummaryRow>) => {
  const { t } = useTranslation();
  const allFilters = [RoleFilter.Checked, RoleFilter.Unchecked]
  const [selectAll, setSelectAll] = useState(true)
  const [filterData, setFilterData] = useState(new Set(allFilters))
  const [selectedValues, setSelectedValues] = useState<Set<RoleFilter>>(new Set());
  const { userManagementStore } = useStores();

  useEffect(() => {
    const currentFilterExist = userManagementStore.getFilterByKey(columnKey)
    if (!selectedValues.size && currentFilterExist && currentFilterExist.filterType === FilterType.Bool) {
      if (currentFilterExist.value) {
        setSelectedValues(new Set([RoleFilter.Checked]))
      } else {
        setSelectedValues(new Set([RoleFilter.Unchecked]))
      }
    }
    // restore state on after dismount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (existingRolesCount) {
      const currentFilterExist = userManagementStore.getFilterByKey(columnKey)
      const key = column.key as keyof typeof existingRolesCount
      const value = existingRolesCount[key]
      const totalUsers = existingRolesCount.totalUsers
      if (!currentFilterExist && value !== undefined && totalUsers !== undefined) {
        if (value === 0) {
          setFilterData(new Set([RoleFilter.Unchecked]))
        } else {
          if (value === totalUsers) {
            setFilterData(new Set([RoleFilter.Checked]))
          } else {
            setFilterData(new Set([RoleFilter.Checked, RoleFilter.Unchecked]))
          }
        }
      } else {
        setFilterData(new Set([RoleFilter.Checked, RoleFilter.Unchecked]))
      }
    }
    // only when existingRolesCount has changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingRolesCount])

  /**
   * Debounced Apply filter; Change observable state and refetch data
   */
  const applyFilter = useDebouncedCallback((filter: UserManagementFilter) => {
    userManagementStore.applyFilter(filter)
  }, FILTER_DEBOUNCE_TIMER)

  /**
   * Debounced Remove filter; Change observable state and refetch data
   */
  const removeFilter = useDebouncedCallback((filterKey: string) => {
    userManagementStore.removeFilter(filterKey)
  }, FILTER_DEBOUNCE_TIMER)

  const handleFilterChange = useCallback((values: Set<RoleFilter>, selectAll: boolean) => {
    const allSelected = selectAll ? values.size === 0 : values.size === filterData.size
    const allUnselected = selectAll ? values.size === filterData.size : values.size === 0
    if (allUnselected) {
      removeFilter.cancel()
      removeFilter(columnKey)
      userManagementStore.addUncheckedColumn(columnKey)
    } else if (allSelected) {
      removeFilter.cancel()
      removeFilter(columnKey)
      userManagementStore.removeUncheckedColumn(columnKey)
    } else {
      applyFilter.cancel()
      applyFilter({
        key: columnKey,
        value: selectAll ? !values.has(RoleFilter.Checked) : values.has(RoleFilter.Checked),
        filterType: FilterType.Bool
      })
      userManagementStore.removeUncheckedColumn(columnKey)
    }
  }, [applyFilter, columnKey, filterData, removeFilter, userManagementStore])

  const handleCheckboxChange = useCallback((item: FilterCheckbox<RoleFilter>) => {
    if (String(item.value) === SELECT_ALL) {
      removeFilter(columnKey)
      setSelectAll(!item.isChecked)
      setSelectedValues(new Set())
      removeFilter.cancel()
      removeFilter(columnKey)
      if (!item.isChecked) {
        userManagementStore.removeUncheckedColumn(columnKey)
      } else {
        userManagementStore.addUncheckedColumn(columnKey)
      }
      return
    }
    const value = item.value
    const newSet = new Set(selectedValues)
    if (newSet.has(value)) {
      newSet.delete(value)
    } else {
      newSet.add(value)
    }
    setSelectedValues(newSet)
    handleFilterChange(newSet, selectAll)
  }, [columnKey, handleFilterChange, removeFilter, selectAll, selectedValues, userManagementStore])

  const getCheckedState = useCallback((value: RoleFilter) => {
    // if select all true or INDETERMINATE
    // check if value is not in a set
    // if select all is false
    // check if value is in a set
    return selectAll ? !selectedValues.has(value) : selectedValues.has(value)
  }, [selectAll, selectedValues])

  const getSelectAllChecked = useCallback(() => {
    if (selectedValues.size) {
      if (selectedValues.size === filterData.size) {
        return !selectAll;
      }
      return INDETERMINATE
    }
    // No checked items return select all state
    return selectAll
  }, [filterData, selectAll, selectedValues])

  // Checked and Unchecked filters
  const filterItems = useMemo(() => (
    [...filterData].map((filter) => ({
      value: filter,
      label: t(`roleFilter.${RoleFilter[filter]}`),
      isChecked: getCheckedState(filter)
    }))
  ), [filterData, t, getCheckedState])

  const selectAllItem = useMemo(() => ({
    value: SELECT_ALL as unknown as RoleFilter,
    label: t("cellFilterSelectAll"),
    isChecked: getSelectAllChecked()
  }), [t, getSelectAllChecked])

  const isActive = userManagementStore.hasActiveFilter(columnKey)
  const hideRows = userManagementStore.hideAllRows() && !userManagementStore.uncheckedColumns.has(columnKey)
  return {
    isActive,
    filterItems: hideRows ? [] : [selectAllItem, ...filterItems],
    handleCheckboxChange,
  }
}